import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { UI_FONT_STACK, roleFontSize } from "./uiTokens.ts";

const STYLE_ID = "first-run-coach-styles";
/** Safety net: if the player wanders off, hide the nudge after a while (without
 *  marking the tutorial seen — a real placement is what dismisses it for good). */
const SAFETY_MS = 14000;

type CoachStep = "idle" | "tap-tower" | "tap-place";

/**
 * BL-212 (S189) — the first-run micro-coach.
 *
 * A wordless, NON-BLOCKING (pointer-events:none) two-step nudge shown on the
 * VERY FIRST run only: ① "TAP A TOWER" pointing at the deploy rail, then on a
 * selection ② "TAP TO PLACE" pointing at the board. It never modals, never
 * blocks input, and clears on the first successful placement (the host then
 * persists MetaState.tutorialSeen so it is never shown again).
 *
 * Pure UI: no game/sim state — the host (main.ts) gates it on tutorialSeen,
 * advances it from the tower-selection callback, and dismisses + persists on
 * the tower.placed event. Reduced motion: the attention pulse is frozen via the
 * @media seam; the static nudge still GUIDES (information preserved).
 */
export class FirstRunCoach {
  private readonly root: HTMLDivElement;
  private readonly tapTowerEl: HTMLDivElement;
  private readonly tapPlaceEl: HTMLDivElement;
  private step: CoachStep = "idle";
  private safetyTimer = 0;

  constructor(parent: HTMLElement) {
    this.injectStyles();
    this.root = document.createElement("div");
    this.root.className = "frc-root";
    this.root.setAttribute("aria-hidden", "true");

    this.tapTowerEl = document.createElement("div");
    this.tapTowerEl.className = "frc-pill frc-tap-tower";
    this.tapTowerEl.style.display = "none";

    this.tapPlaceEl = document.createElement("div");
    this.tapPlaceEl.className = "frc-pill frc-tap-place";
    this.tapPlaceEl.style.display = "none";

    this.root.appendChild(this.tapTowerEl);
    this.root.appendChild(this.tapPlaceEl);
    parent.appendChild(this.root);
  }

  isActive(): boolean {
    return this.step !== "idle";
  }

  /** Step ① — nudge the player to pick a tower from the deploy rail. */
  start(): void {
    if (this.step !== "idle") return;
    this.step = "tap-tower";
    this.tapTowerEl.textContent = t("coach.tapTower");
    this.tapTowerEl.style.display = "block";
    this.tapPlaceEl.style.display = "none";
    this.armSafety();
  }

  /** Step ② — a tower is selected; nudge them to tap the lit path. */
  advanceToPlace(): void {
    if (this.step !== "tap-tower") return;
    this.step = "tap-place";
    this.tapTowerEl.style.display = "none";
    this.tapPlaceEl.textContent = t("coach.tapPlace");
    this.tapPlaceEl.style.display = "block";
    this.armSafety();
  }

  /** Clear the coach for good (first placement, or teardown). */
  dismiss(): void {
    this.step = "idle";
    this.tapTowerEl.style.display = "none";
    this.tapPlaceEl.style.display = "none";
    this.clearSafety();
  }

  destroy(): void {
    this.dismiss();
    this.root.remove();
  }

  private armSafety(): void {
    this.clearSafety();
    if (typeof window === "undefined") return;
    this.safetyTimer = window.setTimeout(() => {
      // Hide the visuals but stay "active" so a later placement still persists
      // the seen flag — we just stop nagging on screen.
      this.tapTowerEl.style.display = "none";
      this.tapPlaceEl.style.display = "none";
    }, SAFETY_MS);
  }

  private clearSafety(): void {
    if (this.safetyTimer && typeof window !== "undefined") {
      window.clearTimeout(this.safetyTimer);
    }
    this.safetyTimer = 0;
  }

  private injectStyles(): void {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      .frc-root {
        position: absolute;
        inset: 0;
        /* BL-212 fix — this root is a permanent full-stage child of #ui-layer,
           whose \`#ui-layer > * { pointer-events: auto }\` (an ID rule,
           index.html) would otherwise WIN over a class \`pointer-events: none\`
           and make this layer swallow every board tap/click from boot. The coach
           is never interactive, so force it off; the pills inherit it. */
        pointer-events: none !important;
        z-index: 12;
      }
      .frc-pill {
        position: absolute;
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("label")};
        font-weight: bold;
        letter-spacing: 2px;
        text-transform: uppercase;
        color: ${COLORS.BG_PRIMARY};
        background: ${COLORS.SENTINEL_GREEN};
        padding: 8px 14px;
        border-radius: 8px;
        white-space: nowrap;
        box-shadow: 0 0 18px ${COLORS.SENTINEL_GREEN};
        animation: frcPulse 1.1s ease-in-out infinite;
      }
      /* ① on the right deploy rail, with a ▶ pointing at it. */
      .frc-tap-tower { right: 5%; top: 46%; }
      .frc-tap-tower::after {
        content: "▶";
        position: absolute; right: -19px; top: 50%;
        transform: translateY(-50%);
        color: ${COLORS.SENTINEL_GREEN};
      }
      /* ② over the board, with a ▼ pointing down at the lit path. */
      .frc-tap-place { left: 50%; top: 22%; transform: translateX(-50%); }
      .frc-tap-place::after {
        content: "▼";
        position: absolute; left: 50%; bottom: -22px;
        transform: translateX(-50%);
        color: ${COLORS.SENTINEL_GREEN};
      }
      @keyframes frcPulse {
        0%, 100% { opacity: 1; box-shadow: 0 0 18px ${COLORS.SENTINEL_GREEN}; }
        50% { opacity: 0.78; box-shadow: 0 0 7px ${COLORS.SENTINEL_GREEN}; }
      }
      /* Reduced motion: freeze the attention pulse — the static nudge still
         guides the player (information preserved, just no pulse). */
      @media (prefers-reduced-motion: reduce) {
        .frc-pill { animation: none; }
      }
    `;
    document.head.appendChild(style);
  }
}
